import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { FC, useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import useTranslate from '../../../i18n/useTranslate'
import { RoutePath } from '../../../router/routesConfig'
import { useExpertiseStyles } from './expertiseShareStyle'
import MyTextfield from '../../../control/textfield/textfield'
import { Form as FinalForm } from 'react-final-form'
import Button from '@material-ui/core/Button'
import { validateEN } from '../../../utilities/validate'
import useExpertises from '../shareLogic/useExpertises'
import useLoading from '../../../control/loading/useLoading'
import Dialog from '../../../control/dialog/dialog'
import { styled } from '@material-ui/core/styles'

const CustomHeader = styled(CardHeader)({
    borderBottom: '1px solid #DCDCDC',
})

const ExpertiseForm: FC<{}> = () => {
    const classes = useExpertiseStyles()
    const translate = useTranslate()
    const params = useParams<{ id: string }>()
    const isEdit = params.id ? true : false
    const history = useHistory()
    const [initialValues, setInitialValues] = useState({
        expertiseID: null,
        expertiseNameTH: null,
        expertiseNameEN: null,
    })
    const expertiseHook = useExpertises()
    const loadingHook = useLoading()
    const saveRef = useRef<Record<string, Function>>()
    const successRef = useRef<Record<string, Function>>()
    const addSuccessIdRef = useRef<number>()
    const duplicateRef = useRef<Record<string, Function>>()
    const [duplicateMsg, setDuplicateMsg] = useState<string>('M028')
    const dirtyRef = useRef<Record<string, Function>>()

    useEffect(() => {
        if (isEdit) {
            getExpertiseByID()
        }
    }, [])

    const handleClose = () => {
        dirtyRef.current?.closeDialog()
        history.push(RoutePath.manageExpertises)
    }

    const submitExpertiseForm = async (values: Record<string, any>) => {
        saveRef.current?.closeDialog()
        loadingHook.loadingOpen()
        if (isEdit) {
            expertiseHook
                .updateExpertiseLUT(Number(params.id), values.expertiseNameTH, values.expertiseNameEN)
                .then((data) => {
                    if (data.expertiseID) {
                        successRef.current?.openDialog()
                    } else {
                        setDuplicateMsg(data.Status)
                        duplicateRef.current?.openDialog()
                    }
                })
                .finally(() => loadingHook.loadingClose())
        } else {
            expertiseHook
                .addExpertiseLUT(values.expertiseNameTH, values.expertiseNameEN)
                .then((data) => {
                    addSuccessIdRef.current = data.expertiseID
                    if (data.expertiseID) {
                        successRef.current?.openDialog()
                    } else {
                        setDuplicateMsg(data.Status)
                        duplicateRef.current?.openDialog()
                    }
                })
                .finally(() => loadingHook.loadingClose())
        }
    }

    const getExpertiseByID = async () => {
        loadingHook.loadingOpen()
        const response = await expertiseHook.getExpertiseByID(Number(params.id))
        if (response && response.result) {
            setInitialValues(response.result)
            // duplicateRef.current?.openDialog()
        }
        loadingHook.loadingClose()
    }

    const bindSaveRef = (func: Record<string, Function>) => {
        saveRef.current = { ...func }
    }

    const bindSuccessRef = (func: Record<string, Function>) => {
        successRef.current = { ...func }
    }

    const bindDuplicateRef = (func: Record<string, Function>) => {
        duplicateRef.current = { ...func }
    }

    const bindDirtyRef = (func: Record<string, Function>) => {
        dirtyRef.current = { ...func }
    }

    const openConfirm = () => {
        saveRef.current?.openDialog()
    }

    const afterCloseSuccess = () => {
        if (isEdit) {
            handleClose()
        } else {
            if (addSuccessIdRef.current) {
                // history.push(RoutePath.expertisesEdit.replace(':id', `${addSuccessIdRef.current}`))
                handleClose()
            }
        }
    }

    const checkDirty = (isDirty: boolean) => {
        if (isDirty) {
            dirtyRef.current?.openDialog()
        } else {
            handleClose()
        }
    }

    return (
        <Container maxWidth="lg">
            <FinalForm initialValues={initialValues} onSubmit={submitExpertiseForm}>
                {({ handleSubmit, dirty, hasValidationErrors }) => (
                    <>
                        <pre />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Card>
                                    <CustomHeader
                                        title={
                                            <Typography variant="h5" className={classes.header}>
                                                {translate.get(
                                                    isEdit ? 'LABEL.EDIT_EXPERTISE_DETAIL' : 'BUTTON.ADD_EXPERTISE',
                                                )}
                                            </Typography>
                                        }
                                    />
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <MyTextfield
                                                    name="expertiseNameTH"
                                                    materialInputProps={{
                                                        label: `${translate.get('LABEL.EXPERTISE_NAME_TH')}`,
                                                        placeholder: `${translate.get('LABEL.EXPERTISE_NAME_TH')}`,
                                                        size: 'small',
                                                    }}
                                                    maxLength={500}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <MyTextfield
                                                    name="expertiseNameEN"
                                                    materialInputProps={{
                                                        label: `${translate.get('LABEL.EXPERTISE_NAME_EN')}`,
                                                        placeholder: `${translate.get('LABEL.EXPERTISE_NAME_EN')}`,
                                                        size: 'small',
                                                    }}
                                                    validates={[validateEN(translate.get('MESSAGE.M001'))]}
                                                    maxLength={500}
                                                    required
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container justify="flex-end" spacing={2}>
                                    <Grid item>
                                        <Button
                                            size={'large'}
                                            variant="outlined"
                                            onClick={() => checkDirty(dirty)}
                                            color="primary"
                                        >
                                            {translate.get('BUTTON.CANCEL')}
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            size={'large'}
                                            variant="contained"
                                            onClick={() => {
                                                if (!hasValidationErrors) {
                                                    openConfirm()
                                                } else {
                                                    handleSubmit()
                                                }
                                            }}
                                            color="primary"
                                        >
                                            {translate.get('BUTTON.SAVE')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Dialog
                            onRef={bindSuccessRef}
                            type="alert-message"
                            title={translate.get('ALERT.TITLE.NOTIFICATION')}
                            content={translate.get('ALERT.CONTENT.SAVE_SUCCESS')}
                            onAfterClose={afterCloseSuccess}
                        />
                        <Dialog
                            onRef={bindDuplicateRef}
                            type="alert-message"
                            title={translate.get('ALERT.TITLE.NOTIFICATION')}
                            content={translate.get(`MESSAGE.${duplicateMsg}`)}
                        />
                        <Dialog
                            onRef={bindSaveRef}
                            type="alert-confirm"
                            title={translate.get('ALERT.TITLE.NOTIFICATION')}
                            content={translate.get('ALERT.CONTENT.NEED_TO_SAVE')}
                            onClickConfirm={handleSubmit}
                        />
                        <Dialog
                            onRef={bindDirtyRef}
                            type="alert-confirm"
                            title={translate.get('ALERT.TITLE.NOTIFICATION')}
                            content={translate.get('ALERT.CONTENT.NEED_TO_CANCEL')}
                            onClickConfirm={handleClose}
                        />
                    </>
                )}
            </FinalForm>
        </Container>
    )
}

export default ExpertiseForm
